import { LoadingButton } from "@mui/lab";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  List,
  Stack,
  Tooltip,
  ListItem,
  IconButton,
  Typography,
  ListItemText,
} from "@mui/material";

import { api } from "../../api";
import { getENV } from "../../../utils/getEnv";
import { constants } from "../../../utils/constants";
import { BulkAddStepProps } from "../BulkAddDialogTypes";
import { downloadFile } from "../../../utils/downloadFile";
import { useGlobalSnackbar } from "../../consecutive-snackbar/GlobalSnackbar";
import { getMultiLevelPropertyValue } from "../../../utils/getMultiLevelPropertyValue";

export const UploadCSV: FunctionComponent<BulkAddStepProps> = ({
  model,
  onNext,
  onImport,
  organizationId,
}) => {
  const { simple } = useGlobalSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [showUpdatableFields, setShowUpdatableFields] = useState(false);
  const [updatableFields, setUpdatableFields] = useState<string[]>([]);

  const handleFileUpload = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!target.files) return;
    const [file] = target.files;
    setFile(file);
  };

  const handleFileDownload = () => {
    const env = getENV();
    const { URL_BASE } = constants.APPS.CORE.ENV[env];
    const apiURL = URL_BASE.replace(/:\d+$/, "");
    const path = `/import/import_entities/${model}/csv_template`;

    const csvTemplateURL = new URL(path, apiURL);
    csvTemplateURL.searchParams.append("target_id", organizationId);
    downloadFile(csvTemplateURL);
  };

  const handleStart = async () => {
    if (!file) return;
    try {
      setIsLoading(true);
      const { data: response } = await api.imports.importCSV({
        files: file,
        importEntity: model,
        targetId: organizationId,
        targetEntity: "organization",
      });
      const { id: importId } = response.data;
      onImport(importId);
      onNext();
    } catch (error) {
      let message = "Unknown network error";
      if (error instanceof Error) {
        const errorMessage = getMultiLevelPropertyValue(
          error,
          "response.data.error.message"
        );
        message = errorMessage ? errorMessage : error.message;
      }
      simple(message, { severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchUpdatableFields = async () => {
      try {
        const { data: response } = await api.imports.getAssetImportFields({
          targetId: organizationId,
          updatable: true,
        });
        setUpdatableFields(response.data);
      } catch (error) {
        let message = "Unknown network error";
        if (error instanceof Error) {
          const errorMessage = getMultiLevelPropertyValue(
            error,
            "response.data.error.message"
          );
          message = errorMessage ? errorMessage : error.message;
        }
        simple(message, { severity: "error" });
      }
    };
    fetchUpdatableFields();
  }, [model, organizationId, simple]);

  return (
    <Box css={(theme) => ({ padding: theme.spacing(2) })}>
      <Typography css={(theme) => ({ padding: `0 ${theme.spacing(2)}` })}>
        Steps
      </Typography>
      <List>
        <ListItem
          secondaryAction={
            <Tooltip placement="top" title="Download">
              <IconButton color="primary" onClick={handleFileDownload}>
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          }
        >
          <ListItemText
            primary={
              <Typography component="span">
                1. Download the CSV template (or use your backup export as a
                starting point)
              </Typography>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <>
                <Typography component="span">
                  2. Edit Your Asset Data
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    Add your asset information to the downloaded template or
                    make changes to your asset export.
                  </li>
                  <li>
                    To update existing assets: AkitaBox uses the{" "}
                    <code>_id</code> column to find the correct asset. Leave a
                    field blank to remove asset data.
                  </li>
                  <li>
                    To create new assets: Any row without an <code>_id</code>{" "}
                    will be added as a new asset.{" "}
                  </li>
                  <li>
                    <span
                      style={{ cursor: "pointer", color: "#1976d2" }}
                      onClick={() =>
                        setShowUpdatableFields(!showUpdatableFields)
                      }
                    >
                      Click here to show/hide all editable fields on your assets
                    </span>
                    <ul
                      css={{ display: showUpdatableFields ? "block" : "none" }}
                    >
                      <li>
                        <strong>{updatableFields.join(", ")}</strong>
                      </li>
                    </ul>
                  </li>
                </ul>
              </>
            }
          />
        </ListItem>
        <ListItem
          secondaryAction={
            <Tooltip placement="top" title="Upload">
              <IconButton
                color="primary"
                component="label"
                htmlFor="upload_csv_input_id"
              >
                <FileUploadIcon />
                <input
                  type="file"
                  role="input"
                  accept=".csv"
                  id="upload_csv_input_id"
                  css={{
                    left: 0,
                    width: 1,
                    bottom: 0,
                    height: 1,
                    overflow: "hidden",
                    position: "absolute",
                    whiteSpace: "nowrap",
                    clip: "rect(0 0 0 0)",
                    clipPath: "inset(50%)",
                  }}
                  onChange={handleFileUpload}
                />
              </IconButton>
            </Tooltip>
          }
        >
          <ListItemText
            primary={
              <>
                <Typography component="span">
                  3. Upload your CSV file to add or update assets
                </Typography>
                {file && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Uploaded file: {file.name}
                  </Typography>
                )}
              </>
            }
          />
        </ListItem>
      </List>
      <Stack
        direction="row"
        justifyContent="flex-end"
        css={(theme) => ({ padding: `0 ${theme.spacing(2)}` })}
      >
        <LoadingButton
          color="primary"
          disableElevation
          loading={isLoading}
          variant="contained"
          onClick={handleStart}
          disabled={!file || isLoading}
        >
          START
        </LoadingButton>
      </Stack>
    </Box>
  );
};
